import { cn } from '@core/utils/cn';

/**
 * Replaces `mat-spinner` / `mat-progress-spinner`.
 *
 * The Angular templates use three sizes: `diameter="18"` as a form-field suffix,
 * `diameter="20"` inside buttons, and the default 40px for full-panel loading states.
 */
interface SpinnerProps {
  /** Diameter in px. Matches the `diameter` input on mat-spinner. */
  size?: number;
  className?: string;
  /** Screen-reader label; omit inside an element that already announces loading. */
  label?: string;
}

export function Spinner({ size = 40, className, label }: SpinnerProps) {
  const stroke = size <= 20 ? 2 : size <= 28 ? 3 : 4;

  return (
    <span
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('vm-spin inline-block flex-none rounded-full', className)}
      style={{
        width: size,
        height: size,
        borderWidth: stroke,
        borderStyle: 'solid',
        borderColor: 'currentColor',
        borderTopColor: 'transparent',
      }}
    />
  );
}

/** Centred spinner with optional caption — the `.loading-state` block. */
export function LoadingState({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="loading-state">
      <Spinner size={40} className="text-accent" label={message} />
      <p>{message}</p>
    </div>
  );
}

/** Full-viewport spinner, used while the shell resolves permissions. */
export function FullPageSpinner({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4">
      <Spinner size={40} className="text-accent" label={message} />
      <p className="text-base text-fg-3">{message}</p>
    </div>
  );
}
